<template>
  <div class="card mb-4">
    <div class="card-body py-3">
      <div class="sales-progress-steps">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="sales-step"
          :class="{ 
            'sales-completed': isStepCompleted(index),
            'sales-active': isStepActive(index),
            'sales-disabled': isStepDisabled(index),
            'sales-clickable': canNavigateToStep(index)
          }"
          @click="goToStep(index)"
        >
          <div class="sales-step-circle">
            <font-awesome-icon 
              v-if="isStepCompleted(index)" 
              icon="check" 
              class="sales-step-icon"
            />
            <span v-else class="sales-step-number">{{ index + 1 }}</span>
          </div>
          <div class="sales-step-content">
            <h6 class="sales-step-title">{{ step.title }}</h6>
            <p class="sales-step-description">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  steps: {
    type: Array,
    required: true
  },
  currentStep: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['goToStep'])

const isStepActive = (stepIndex) => {
  return props.currentStep === stepIndex
}

const isStepCompleted = (stepIndex) => {
  return props.currentStep > stepIndex
}

const isStepDisabled = (stepIndex) => {
  return props.currentStep < stepIndex
}

const canNavigateToStep = (stepIndex) => {
  return stepIndex <= props.currentStep
}

const goToStep = (stepIndex) => {
  if (canNavigateToStep(stepIndex)) {
    emit('goToStep', stepIndex)
  }
}
</script>

<style scoped>
/* Progress Steps */
.sales-progress-steps {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  padding: 1rem 0;
}

.sales-progress-steps::before {
  content: '';
  position: absolute;
  top: 30px;
  left: 50px;
  right: 50px;
  height: 3px;
  background: #e9ecef;
  z-index: 1;
  border-radius: 2px;
}

.sales-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 2;
  flex: 1;
  max-width: 200px;
  padding: 0 1rem;
}

.sales-step-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 3px solid transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.sales-step.sales-active .sales-step-circle {
  background: var(--bs-primary);
  color: white;
  border-color: #fff;
  box-shadow: 0 4px 15px rgba(13, 110, 253, 0.3);
  transform: scale(1.1);
}

.sales-step.sales-completed .sales-step-circle {
  background: #198754;
  color: white;
  border-color: #fff;
  box-shadow: 0 4px 15px rgba(25, 135, 84, 0.3);
}

.sales-step.sales-disabled .sales-step-circle {
  background: #f8f9fa;
  color: #adb5bd;
  border-color: #e9ecef;
}

.sales-step-number {
  font-weight: 700;
  font-size: 18px;
}

.sales-step-icon {
  font-size: 16px;
}

.sales-step-title {
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 16px;
  color: #495057;
}

.sales-step-description {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sales-progress-steps {
    flex-direction: column;
    gap: 2rem;
    padding: 2rem 0;
  }
  
  .sales-progress-steps::before {
    display: none;
  }
  
  .sales-step {
    flex-direction: row;
    text-align: left;
    max-width: none;
    padding: 1rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
  
  .sales-step-circle {
    margin-right: 1.5rem;
    margin-bottom: 0;
    flex-shrink: 0;
  }
}
</style>
